import React, { createContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface User {
  userId: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
}

interface TokenResponse {
  success: boolean;
  token: string;
  expiresIn: string;
  user: {
    id: string;
    email: string;
    name: string;
  };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (userData: User) => Promise<void>;
  logout: () => void;
  isTokenValid: () => boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isAuthenticated: false,
  login: async () => {},
  logout: () => {},
  isTokenValid: () => false,
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check for existing session on mount
    const storedUser = localStorage.getItem('sso_user');
    const storedToken = localStorage.getItem('jwt_token');
    const tokenExpiry = localStorage.getItem('token_expiry');
    const isAuth = localStorage.getItem('sso_authenticated');

    if (storedUser && storedToken && isAuth === 'true') {
      // Check if token is still valid
      if (tokenExpiry && new Date().getTime() < parseInt(tokenExpiry)) {
        setUser(JSON.parse(storedUser));
        setToken(storedToken);
        setIsAuthenticated(true);
      } else {
        // Token expired, clear everything and redirect
        logout();
      }
    }
  }, []);

  const isTokenValid = (): boolean => {
    const tokenExpiry = localStorage.getItem('token_expiry');
    if (!tokenExpiry || !token) return false;
    return new Date().getTime() < parseInt(tokenExpiry);
  };

  const fetchToken = async (userData: User): Promise<string | null> => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      const tokenPayload = {
        UID: userData.userId,
        Email: userData.email,
        FirstName: userData.firstName || userData.name.split(' ')[0],
        LastName: userData.lastName || userData.name.split(' ').slice(1).join(' '),
      };

      console.log('=== Fetching JWT Token ===');
      console.log('API Base URL:', apiBaseUrl);
      console.log('Token request payload:', tokenPayload);

      const response = await axios.post<TokenResponse>(`${apiBaseUrl}/auth/token`, tokenPayload);

      console.log('Token response:', response.data);

      if (response.data.success && response.data.token) {
        const jwtToken = response.data.token;

        // Calculate expiry time (24 hours from now)
        const expiryTime = new Date().getTime() + 24 * 60 * 60 * 1000;

        console.log('JWT Token obtained successfully');
        console.log('Token expiry time:', new Date(expiryTime).toISOString());

        // Store token and expiry
        localStorage.setItem('jwt_token', jwtToken);
        localStorage.setItem('token_expiry', expiryTime.toString());

        return jwtToken;
      }

      console.error('Token response invalid - success or token missing');
      return null;
    } catch (error) {
      console.error('=== Failed to fetch token ===');
      console.error('Error:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      return null;
    }
  };

  const login = async (userData: User) => {
    console.log('=== Login Method Called ===');
    console.log('User data:', userData);

    // Fetch JWT token
    const jwtToken = await fetchToken(userData);

    if (jwtToken) {
      console.log('Setting user state and authentication');
      setUser(userData);
      setToken(jwtToken);
      setIsAuthenticated(true);

      // Store user data
      localStorage.setItem('sso_user', JSON.stringify(userData));
      localStorage.setItem('sso_authenticated', 'true');
      localStorage.setItem('sso_timestamp', new Date().toISOString());

      console.log('Login completed successfully');
      console.log('LocalStorage items set:', {
        sso_user: localStorage.getItem('sso_user'),
        sso_authenticated: localStorage.getItem('sso_authenticated'),
        jwt_token: localStorage.getItem('jwt_token') ? 'present' : 'missing',
      });
    } else {
      console.error('Failed to obtain JWT token, throwing error');
      throw new Error('Failed to obtain authentication token');
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('sso_user');
    localStorage.removeItem('sso_authenticated');
    localStorage.removeItem('sso_timestamp');
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('token_expiry');
    sessionStorage.clear();

    // Use Microsoft OAuth logout endpoint to clear the session
    // After logout, Microsoft will redirect back to the frontend origin
    // The frontend (SSOCallback) will then redirect to SAML login since there are no SSO params
    const tenantId = '64bf5edc-e800-479b-84e5-23aa7e8cd7dd';
    const postLogoutRedirectUri = encodeURIComponent(window.location.origin);

    const logoutUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout`;

    console.log('Logging out and redirecting to:', logoutUrl);
    window.location.href = logoutUrl;
  };

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, login, logout, isTokenValid }}>
      {children}
    </AuthContext.Provider>
  );
};
